import React from "react";

// Minimal markdown renderer for AI assistant chat output. Covers the
// constructs the model actually emits — headings, bold, italic, inline code,
// bullet/numbered lists, links, and line breaks — without pulling in a full
// CommonMark dependency. Not a complete parser; just enough to stop showing
// raw **asterisks** and `backticks` to the user.

const INLINE =
  /(\*\*[^*]+\*\*|__[^_]+__)|(\*[^*\n]+\*|_[^_\n]+_)|(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))/g;

function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (m[1]) {
      out.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else if (m[2]) {
      out.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    } else if (m[3]) {
      out.push(
        <code key={key++} className="px-1 py-0.5 rounded-sm bg-muted/70 font-mono text-[0.85em]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (m[4]) {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!;
      out.push(
        <a
          key={key++}
          href={lm[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-primary"
        >
          {lm[1]}
        </a>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isBullet = (l: string) => /^\s*[-*]\s+/.test(l);
const isOrdered = (l: string) => /^\s*\d+\.\s+/.test(l);
const isHeading = (l: string) => /^#{1,6}\s+/.test(l);

export function ChatMarkdown({ content }: { content: string }) {
  const lines = content.replace(/\r\n/g, "\n").split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      i++;
      continue;
    }

    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push(
        <p key={key++} className="font-semibold">
          {renderInline(h[2])}
        </p>,
      );
      i++;
      continue;
    }

    if (isBullet(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && isBullet(lines[i])) {
        items.push(<li key={items.length}>{renderInline(lines[i].replace(/^\s*[-*]\s+/, ""))}</li>);
        i++;
      }
      blocks.push(
        <ul key={key++} className="list-disc pl-5 space-y-0.5">
          {items}
        </ul>,
      );
      continue;
    }

    if (isOrdered(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && isOrdered(lines[i])) {
        items.push(<li key={items.length}>{renderInline(lines[i].replace(/^\s*\d+\.\s+/, ""))}</li>);
        i++;
      }
      blocks.push(
        <ol key={key++} className="list-decimal pl-5 space-y-0.5">
          {items}
        </ol>,
      );
      continue;
    }

    // Paragraph: consecutive plain lines (joined with <br/>).
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !isBullet(lines[i]) &&
      !isOrdered(lines[i]) &&
      !isHeading(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++}>
        {para.map((p, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <br />}
            {renderInline(p)}
          </React.Fragment>
        ))}
      </p>,
    );
  }

  return <div className="space-y-2 text-sm leading-relaxed">{blocks}</div>;
}
